import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Camera, Crown, Coins, MessageCircle, UserPlus, UserCheck, Ban, VolumeX, Flag, Loader2, Store } from "lucide-react";
import { api } from "@/lib/api";
import { useWalkie, Avatar, notify } from "./WalkieLayout";
import TipDialog from "./TipDialog";

const THEMES = { cyan: "#00F0FF", magenta: "#FF2BD6", lime: "#39FF14", amber: "#FFB000", galaxy: "#7B2BFF", holo: "#9EFFF5" };
const BADGE_LABEL = { founder: "Founder", premium: "Premium", bdg_golden_mic: "Golden Mic", bdg_legend: "Legend" };

function Cover({ p, editable, onUpload }) {
  const accent = THEMES[p.theme] || THEMES.cyan;
  return (
    <div className="relative h-36 rounded-3xl overflow-hidden" style={{ background: p.cover_url ? undefined : `linear-gradient(120deg, ${accent}66, #FF2BD655)` }}>
      {p.cover_url && <img src={p.cover_url} alt="" className="w-full h-full object-cover" />}
      {p.premium && (p.theme === "galaxy" || p.theme === "holo") && <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-transparent via-white/10 to-transparent" />}
      {editable && <UploadBtn kind="cover" onUpload={onUpload} className="absolute right-3 bottom-3" testId="walkie-cover-upload" />}
    </div>
  );
}

function UploadBtn({ kind, onUpload, className, testId }) {
  const ref = useRef(null); const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("kind", kind); fd.append("file", f);
    try { await api.post("/walkie/me/media", fd); toast.success(`${kind === "avatar" ? "Profile picture" : "Cover photo"} updated`); onUpload(); } catch (er) { notify.err(er); }
    setBusy(false); e.target.value = "";
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden onChange={pick} data-testid={`${testId}-input`} />
      <button onClick={() => ref.current?.click()} data-testid={testId} className={`${className} w-9 h-9 rounded-full bg-black/60 text-white flex items-center justify-center`} aria-label={`Change ${kind}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
      </button>
    </>
  );
}

function Header({ p, editable, onUpload, extra }) {
  return (
    <div>
      <Cover p={p} editable={editable} onUpload={onUpload} />
      <div className="px-2 -mt-10 flex items-end gap-3">
        <div className="relative"><Avatar p={p} size={84} />{editable && <UploadBtn kind="avatar" onUpload={onUpload} className="absolute -right-1 bottom-0" testId="walkie-avatar-upload" />}</div>
        <div className="flex-1 min-w-0 pb-1">
          <div className="font-black text-xl truncate" data-testid="walkie-profile-name">{p.display_name}{p.premium && <Crown className="inline w-4 h-4 ml-1 text-[#FFE300]" />}</div>
          <div className="text-xs wt-muted">@{p.username} · {p.online ? <span className="text-[#39FF14]">online</span> : p.online === false ? "offline" : "status hidden"}</div>
        </div>
        {extra}
      </div>
      {p.bio && <p className="text-sm mt-3 px-2">{p.bio}</p>}
      <div className="flex gap-4 px-2 mt-3 text-sm">
        <span><b>{p.followers}</b> <span className="wt-muted">followers</span></span>
        <span><b>{p.following}</b> <span className="wt-muted">following</span></span>
      </div>
      {p.badges?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-2 mt-3">
          {p.badges.map((b) => <span key={b} className={`text-[10px] font-black uppercase px-2 py-1 rounded-full ${b === p.active_badge ? "bg-[#FFE300] text-black" : "wt-card"}`}>{BADGE_LABEL[b] || b}</span>)}
        </div>
      )}
    </div>
  );
}

export function WalkieUser() {
  const { uid } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [tip, setTip] = useState(false);
  const load = () => api.get(`/walkie/users/${uid}`).then((r) => setD(r.data)).catch((e) => { notify.err(e); nav("/walkie"); });
  useEffect(() => { load(); }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <div className="py-16 text-center wt-muted animate-pulse">Loading…</div>;
  const act = (path, msg) => api.post(path).then((r) => { toast.success(msg(r.data)); load(); }).catch(notify.err);
  const dm = () => api.post(`/walkie/dm/${uid}`).then((r) => nav(`/walkie/c/${r.data.channel_id}`)).catch(notify.err);
  const report = () => { const reason = window.prompt("Why are you reporting this member?"); if (reason) api.post("/walkie/report", { target_type: "user", target_id: uid, reason }).then(() => toast.success("Report sent to moderators")); };
  return (
    <div className="space-y-5" data-testid="walkie-user-profile">
      <Header p={d.profile} />
      <div className="text-xs wt-muted px-2">{d.tips_received} coins in tips received</div>
      <div className="grid grid-cols-3 gap-2">
        <button onClick={() => act(`/walkie/users/${uid}/follow`, (r) => (r.following ? "Following" : "Unfollowed"))} data-testid="walkie-follow-btn"
          className={`py-2.5 rounded-full font-black text-sm flex items-center justify-center gap-1 ${d.is_following ? "wt-card" : "bg-[#FF2BD6] text-black"}`}>
          {d.is_following ? <><UserCheck className="w-4 h-4" /> Following</> : <><UserPlus className="w-4 h-4" /> Follow</>}
        </button>
        <button onClick={dm} data-testid="walkie-dm-btn" className="py-2.5 rounded-full font-black text-sm wt-card flex items-center justify-center gap-1"><MessageCircle className="w-4 h-4" /> Talk</button>
        <button onClick={() => setTip(true)} data-testid="walkie-profile-tip-btn" className="py-2.5 rounded-full font-black text-sm bg-[#FFE300] text-black flex items-center justify-center gap-1"><Coins className="w-4 h-4" /> Tip</button>
      </div>
      <div className="flex gap-4 text-xs wt-muted px-2">
        <button onClick={() => act(`/walkie/users/${uid}/mute`, (r) => (r.mute ? "Muted" : "Unmuted"))} data-testid="walkie-mute-btn" className="flex items-center gap-1"><VolumeX className="w-3.5 h-3.5" /> {d.is_muted ? "Unmute" : "Mute"}</button>
        <button onClick={() => act(`/walkie/users/${uid}/block`, (r) => (r.block ? "Blocked" : "Unblocked"))} data-testid="walkie-block-btn" className="flex items-center gap-1"><Ban className="w-3.5 h-3.5" /> {d.is_blocked ? "Unblock" : "Block"}</button>
        <button onClick={report} data-testid="walkie-report-user-btn" className="flex items-center gap-1"><Flag className="w-3.5 h-3.5" /> Report</button>
      </div>
      {tip && <TipDialog to={d.profile} onClose={() => setTip(false)} />}
    </div>
  );
}

export default function WalkieProfile() {
  const { me, reloadMe, outbox, status } = useWalkie();
  const p = { ...me.profile, online: me.profile.privacy?.show_online === false ? null : status === "online" };
  const [f, setF] = useState({ display_name: p.display_name, username: p.username, bio: p.bio });
  const [autoSend, setAutoSend] = useState(localStorage.getItem("wt_autosend") === "1");
  const save = async (patch) => { try { await api.patch("/walkie/me", patch); await reloadMe(); toast.success("Saved"); } catch (e) { notify.err(e); } };
  const owned = p.owned || [];
  const themes = ["cyan", "magenta", "lime", "amber", ...(p.premium ? ["galaxy", "holo"] : [])];
  return (
    <div className="space-y-5" data-testid="walkie-profile">
      <Header p={p} editable onUpload={reloadMe} extra={<Link to="/walkie/store" data-testid="walkie-profile-store" className="text-xs font-black bg-[#FFE300] text-black px-3 py-2 rounded-full flex items-center gap-1"><Store className="w-3.5 h-3.5" />{me.coins}</Link>} />
      <section className="wt-card rounded-2xl p-4 space-y-3">
        <div className="font-black text-sm">Edit profile</div>
        <input value={f.display_name} onChange={(e) => setF({ ...f, display_name: e.target.value })} maxLength={40} placeholder="Display name" data-testid="walkie-edit-name" className="wt-input w-full rounded-xl px-3 py-2 outline-none" />
        <input value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} maxLength={20} placeholder="username" data-testid="walkie-edit-username" className="wt-input w-full rounded-xl px-3 py-2 outline-none" />
        <textarea value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} maxLength={300} rows={2} placeholder="Bio" data-testid="walkie-edit-bio" className="wt-input w-full rounded-xl px-3 py-2 outline-none resize-none" />
        <button onClick={() => save(f)} data-testid="walkie-save-profile" className="px-5 py-2 rounded-full bg-[#FF2BD6] text-black font-black text-sm">Save</button>
      </section>
      <section className="wt-card rounded-2xl p-4 space-y-3">
        <div className="font-black text-sm">Customize</div>
        <div className="flex flex-wrap gap-2">
          {themes.map((t) => <button key={t} onClick={() => save({ theme: t })} data-testid={`walkie-theme-${t}`} className={`w-9 h-9 rounded-full ring-2 ${p.theme === t ? "ring-current" : "ring-transparent"}`} style={{ background: THEMES[t], color: THEMES[t] }} aria-label={`${t} theme`} />)}
          {!p.premium && <span className="text-[11px] wt-muted self-center">Premium unlocks animated Galaxy & Holo themes</span>}
        </div>
        <Picker label="Voice effect" value={p.voice_effect} options={["none", ...owned.filter((o) => o.startsWith("fx_"))]} onPick={(v) => save({ voice_effect: v })} testId="walkie-voice-effect" />
        <Picker label="Profile decoration" value={p.decoration} options={["", ...owned.filter((o) => o.startsWith("dec_"))]} onPick={(v) => save({ decoration: v })} testId="walkie-decoration" />
        <Picker label="Showcase badge" value={p.active_badge} options={["", ...(p.badges || [])]} onPick={(v) => save({ active_badge: v })} testId="walkie-badge" />
      </section>
      <section className="wt-card rounded-2xl p-4 space-y-2" data-testid="walkie-privacy">
        <div className="font-black text-sm">Privacy</div>
        <label className="flex justify-between text-sm items-center">Who can start private conversations
          <select value={p.privacy.allow_dms} onChange={(e) => save({ privacy: { allow_dms: e.target.value } })} data-testid="walkie-privacy-dms" className="wt-input rounded-lg px-2 py-1 text-xs">
            <option value="everyone">Everyone</option><option value="followers">Followers</option><option value="nobody">Nobody</option>
          </select></label>
        <Toggle label="Show my online status" on={p.privacy.show_online} onClick={() => save({ privacy: { show_online: !p.privacy.show_online } })} testId="walkie-privacy-online" />
        <Toggle label="Appear in member search" on={p.privacy.discoverable} onClick={() => save({ privacy: { discoverable: !p.privacy.discoverable } })} testId="walkie-privacy-discover" />
        <div className="text-[11px] wt-muted">{p.blocked.length} blocked · {p.muted.length} muted</div>
      </section>
      <section className="wt-card rounded-2xl p-4 space-y-2" data-testid="walkie-offline-settings">
        <div className="font-black text-sm">Offline voice messages</div>
        <Toggle label="Automatically send recordings when I reconnect" on={autoSend} testId="walkie-autosend-toggle"
          onClick={() => { const v = !autoSend; setAutoSend(v); localStorage.setItem("wt_autosend", v ? "1" : "0"); }} />
        <div className="flex items-center justify-between text-sm"><span className="wt-muted">{outbox.count} queued</span>
          <button disabled={!outbox.count} onClick={outbox.flush} data-testid="walkie-send-queued" className="text-xs font-black px-3 py-1.5 rounded-full bg-[#00F0FF] text-black disabled:opacity-40">Send queued</button></div>
      </section>
    </div>
  );
}

function Picker({ label, value, options, onPick, testId }) {
  return (
    <label className="flex justify-between items-center text-sm">{label}
      <select value={value || ""} onChange={(e) => onPick(e.target.value)} data-testid={testId} className="wt-input rounded-lg px-2 py-1 text-xs">
        {options.map((o) => <option key={o} value={o}>{o === "" ? "None" : o === "none" ? "Natural" : o.replace(/^(fx|dec|bdg)_/, "").replace("_", " ")}</option>)}
      </select>
    </label>
  );
}

function Toggle({ label, on, onClick, testId }) {
  return (
    <label className="flex justify-between items-center text-sm cursor-pointer">{label}
      <input type="checkbox" checked={!!on} onChange={onClick} data-testid={testId} className="w-5 h-5 accent-[#FF2BD6]" />
    </label>
  );
}
